import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RotateCcw, Plus, Pencil, Trash2 } from 'lucide-react';
import { marketApi } from '@/api';
import type { Plan } from '@/api/market';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  adminDrawerFooterClass,
  adminDrawerFormPaddingClass,
  adminDrawerScrollAreaClass,
  adminDrawerHeaderClass,
  adminDrawerShell,
  adminDrawerTitleClass,
  alertDialogDarkClass,
  alertDialogDescDark,
  alertDialogTitleDark,
  btnDangerSolid,
  btnIconDangerGhost,
  btnIconGhostNeon,
  btnOutlineNeon,
  btnPrimaryNeon,
  fieldClass,
  labelMutedClass,
} from '@/lib/admin-ui';
import { usePlatformPermission } from '@/hooks/use-platform-permission';
import { useUIStore } from '@/store/useUIStore';

export function PlanConfigPage() {
  const isLight = useUIStore((s) => s.theme === 'light');
  const lightPrimaryBtn =
    'border border-cyan-400/45 bg-cyan-500 text-slate-950 hover:bg-cyan-400';
  const lightOutlineBtn =
    'border border-slate-300 bg-white text-slate-700 hover:border-cyan-400/45 hover:bg-cyan-50 hover:text-cyan-700';
  const lightFieldClass =
    'border-slate-300 bg-white text-slate-800 placeholder:text-slate-400 focus-visible:ring-cyan-500/35';
  const canMutateMarket = usePlatformPermission('platform:market:mutate');
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState<Plan | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    key: '',
    price: 0,
    description: '',
    features: '',
  });

  useEffect(() => {
    loadPlans();
  }, []);

  async function loadPlans() {
    try {
      setLoading(true);
      // 直接获取所有订阅版本模板，不依赖应用系统
      const data = await marketApi.getAllPlans();
      // 将价格从分转换为元
      const plansWithYuan = (Array.isArray(data) ? data : []).map(plan => ({
        ...plan,
        price: plan.price / 100,
      }));
      setPlans(plansWithYuan);
    } catch (error) {
      console.error('加载版本模板失败:', error);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }

  function handleAdd() {
    setEditingPlan(null);
    setFormData({
      name: '',
      key: '',
      price: 0,
      description: '',
      features: '',
    });
    setDialogOpen(true);
  }

  function handleEdit(plan: Plan) {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      key: plan.key,
      price: plan.price,
      description: plan.description || '',
      features: plan.features.map(f => f.name).join('\n'),
    });
    setDialogOpen(true);
  }

  async function handleDelete(plan: Plan) {
    setPlanToDelete(plan);
    setDeleteDialogOpen(true);
  }

  async function confirmDelete() {
    if (!planToDelete) return;
    
    try {
      await marketApi.deletePlan(planToDelete.id);
      setPlans(plans.filter(p => p.id !== planToDelete.id));
      setDeleteDialogOpen(false);
      setPlanToDelete(null);
    } catch (error) {
      console.error('删除订阅版本失败:', error);
      toast.error('删除失败，请重试');
    }
  }

  async function handleSubmit() {
    // 处理功能特点：分割、过滤空行，然后转换为对象数组
    const featuresArray = formData.features
      .split('\n')
      .map(f => f.trim())
      .filter(f => f.length > 0);
    
    const featuresObjects = featuresArray.map((f, i) => ({
      key: `feature_${i}`,
      name: f,
      limit: '0',
    }));
    
    try {
      // 将元转换为分（数据库存储单位是分）
      const priceInCents = Math.round(formData.price * 100);
      
      if (editingPlan) {
        // 编辑模式
        await marketApi.updatePlan({
          id: editingPlan.id,
          name: formData.name,
          key: formData.key,
          price: priceInCents,
          description: formData.description,
          features: featuresObjects,
        });
        setPlans((plans.map(p =>
          p.id === editingPlan.id
            ? {
                ...p,
                name: formData.name,
                key: formData.key,
                price: formData.price,
                description: formData.description,
                features: featuresObjects,
              }
            : p
        )) as Plan[]);
      } else {
        // 新增模式
        const newPlan = await marketApi.createPlan({
          name: formData.name,
          key: formData.key,
          price: priceInCents,
          description: formData.description,
          features: featuresObjects,
        });
        setPlans([...plans, { ...newPlan, price: formData.price }]);
      }
      
      setDialogOpen(false);
    } catch (error) {
      console.error('保存订阅版本失败:', error);
      toast.error('保存失败，请重试');
    }
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">订阅版本配置</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            管理全局订阅版本模板（价格与功能点）
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className={cn(isLight ? lightOutlineBtn : btnOutlineNeon)}
            onClick={loadPlans}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            刷新
          </Button>
          {canMutateMarket && (
            <Button className={cn(isLight ? lightPrimaryBtn : btnPrimaryNeon)} onClick={handleAdd}>
              <Plus className="mr-2 h-4 w-4" />
              新增版本
            </Button>
          )}
        </div>
      </div>

      {/* 版本模板列表 */}
      {loading ? (
        <div className="text-center py-8 text-muted-foreground">加载中...</div>
      ) : plans.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          暂无版本模板数据
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {plans.map((plan) => (
            <Card key={plan.id} className="flex flex-col">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{plan.name}</CardTitle>
                    <Badge variant="outline" className="mt-1">{plan.key}</Badge>
                  </div>
                  {canMutateMarket && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className={btnIconGhostNeon}
                        onClick={() => handleEdit(plan)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={btnIconDangerGhost}
                        onClick={() => handleDelete(plan)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
                <CardDescription className="text-2xl font-bold text-primary mt-2">
                  ¥{plan.price}
                  <span className="text-sm font-normal text-muted-foreground">/月</span>
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">功能特点：</h4>
                  <ul className="space-y-1">
                    {plan.features.map((feature, i) => (
                      <li key={feature.key || i} className="text-sm text-muted-foreground flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                        {feature.name}
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* 使用说明 */}
      <Card>
        <CardHeader>
          <CardTitle>使用说明</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>• 这里维护的是全局订阅版本模板，不关联具体应用</p>
          <p>• 可以在「应用市场配置」中为每个应用选择可购买的版本</p>
          <p>• 修改模板价格会影响所有引用该模板的应用</p>
          <p>• 建议先配置好版本模板，再配置应用市场</p>
        </CardContent>
      </Card>

      {/* 新增/编辑抽屉 */}
      <Drawer
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        direction="right"
        shouldScaleBackground={false}
      >
        <DrawerContent
          className={cn(
            adminDrawerShell('narrow'),
            isLight &&
              'border-slate-300 bg-white text-slate-900 [&>button]:border-slate-300 [&>button]:bg-white [&>button]:text-slate-500 [&>button]:hover:border-cyan-400/45 [&>button]:hover:bg-cyan-50 [&>button]:hover:text-cyan-700',
          )}
          data-admin-shell
        >
          <DrawerHeader className={cn(adminDrawerHeaderClass, isLight && 'border-b border-slate-200 bg-white')}>
            <DrawerTitle className={adminDrawerTitleClass}>
              {editingPlan ? '编辑订阅版本' : '新增订阅版本'}
            </DrawerTitle>
            <DrawerDescription className={cn('text-sm', isLight ? 'text-slate-500' : 'text-zinc-500')}>
              {editingPlan ? '修改订阅版本模板信息' : '创建新的订阅版本模板'}
            </DrawerDescription>
          </DrawerHeader>

          <div className={cn(adminDrawerScrollAreaClass, isLight && 'bg-white')}>
            <div className={cn(adminDrawerFormPaddingClass, 'space-y-4')}>
              <div className="grid gap-2">
                <Label htmlFor="name" className={labelMutedClass}>
                  版本名称
                </Label>
                <Input
                  id="name"
                  placeholder="例如：基础版"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={cn(fieldClass, isLight && lightFieldClass)}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="key" className={labelMutedClass}>
                  版本标识
                </Label>
                <Input
                  id="key"
                  placeholder="例如：basic"
                  value={formData.key}
                  onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                  className={cn(fieldClass, isLight && lightFieldClass)}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="price" className={labelMutedClass}>
                  月费价格（元）
                </Label>
                <Input
                  id="price"
                  type="number"
                  placeholder="0"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: Number(e.target.value) })
                  }
                  className={cn(fieldClass, isLight && lightFieldClass)}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="description" className={labelMutedClass}>
                  描述
                </Label>
                <Textarea
                  id="description"
                  placeholder="版本描述..."
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={3}
                  className={cn(fieldClass, isLight && lightFieldClass)}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="features" className={labelMutedClass}>
                  功能特点（每行一个）
                </Label>
                <Textarea
                  id="features"
                  placeholder="完整基础功能&#10;10 用户额度&#10;邮件支持"
                  value={formData.features}
                  onChange={(e) =>
                    setFormData({ ...formData, features: e.target.value })
                  }
                  rows={3}
                  className={cn(fieldClass, isLight && lightFieldClass)}
                />
              </div>
            </div>
          </div>

          <div className={cn(adminDrawerFooterClass, isLight && 'border-slate-200 bg-white')}>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={!canMutateMarket}
              className={cn('flex-1', isLight ? lightPrimaryBtn : btnPrimaryNeon)}
            >
              {editingPlan ? '保存' : '创建版本'}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      {/* 删除确认 */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className={alertDialogDarkClass}>
          <AlertDialogHeader>
            <AlertDialogTitle className={alertDialogTitleDark}>确认删除</AlertDialogTitle>
            <AlertDialogDescription className={alertDialogDescDark}>
              确定要删除订阅版本 &quot;{planToDelete?.name}&quot; 吗？此操作不可恢复。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={btnOutlineNeon}>取消</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className={btnDangerSolid}>
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
